import { useState, useEffect, memo, useCallback, type CSSProperties } from 'react'
import { programCategoriesApi } from '../../lib/api.js'
import { useStore } from '../../store/index.js'
import type { ProgramCategory } from '../../types/domain.js'
import { ProgramCategoryModal } from './ProgramCategoryModal.js'
import { TaxonomyReorderButtons } from './TaxonomyReorderButtons.js'
import { Spinner } from '../common/Spinner.js'
import './ProgramsView.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface ProgramCategoriesPanelProps {
  channelId: string
  canManage?: boolean
}

// ─── Composant ────────────────────────────────────────────────────────────────

export const ProgramCategoriesPanel = memo(function ProgramCategoriesPanel({ channelId, canManage = true }: ProgramCategoriesPanelProps) {
  const programsUpdateSeq = useStore(state => state.programsUpdateSeq)
  const [categories, setCategories] = useState<ProgramCategory[]>([])
  const [loading,    setLoading]    = useState(true)
  const [showModal,  setShowModal]  = useState(false)
  const [editing,    setEditing]    = useState<ProgramCategory | undefined>(undefined)
  const [search,     setSearch]     = useState('')

  const [sortOrder,  setSortOrder]  = useState<'manual' | 'name-asc' | 'name-desc' | 'count-desc'>('manual')
  const [error,      setError]      = useState<string | null>(null)

  const reload = useCallback((silent = false) => {
    if (!silent) setLoading(true)
    programCategoriesApi.list({ channelId })
      .then(setCategories)
      .finally(() => setLoading(false))
  }, [channelId])

  useEffect(() => { reload(categories.length > 0) }, [reload, programsUpdateSeq])

  const handleCreated = useCallback((category: ProgramCategory) => {
    setCategories(prev => [...prev, category].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)))
    setShowModal(false)
  }, [])

  const handleUpdated = useCallback((category: ProgramCategory) => {
    setCategories(prev => prev.map(c => c.id === category.id ? category : c))
    setEditing(undefined)
    setShowModal(false)
  }, [])

  const handleDeleted = useCallback((id: string) => {
    setCategories(prev => prev.filter(c => c.id !== id))
  }, [])

  const handleEdit = useCallback((category: ProgramCategory, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditing(category)
    setShowModal(true)
  }, [])

  /**
   * Réordonnancement optimiste : l'ordre se repose tout de suite et se rétablit
   * si l'API refuse.
   */
  const handleReorder = useCallback(async (nextIds: string[]) => {
    const previous = categories
    const byId = new Map(categories.map(category => [category.id, category]))
    setCategories(nextIds.map((id, index) => ({ ...byId.get(id)!, sortOrder: index })))
    setError(null)
    try {
      await programCategoriesApi.reorder(nextIds[0], nextIds)
    } catch (cause) {
      setCategories(previous)
      setError(cause instanceof Error ? cause.message : 'Réordonnancement impossible')
    }
  }, [categories])

  const handleNew = useCallback(() => {
    setEditing(undefined)
    setShowModal(true)
  }, [])

  // L'ordre manuel se pose sur la liste complète, jamais sur un résultat filtré
  // ou trié autrement.
  const manualIds = [...categories]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, 'fr'))
    .map(category => category.id)
  const searching = search.trim().length > 0
  const canReorder = canManage && sortOrder === 'manual' && !searching

  const filtered = categories.filter(c =>
    !search || c.label.toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => {
    if (sortOrder === 'name-asc') return a.label.localeCompare(b.label, 'fr')
    if (sortOrder === 'name-desc') return b.label.localeCompare(a.label, 'fr')
    if (sortOrder === 'count-desc') return (b._count?.programs ?? 0) - (a._count?.programs ?? 0)
    return a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, 'fr')
  })

  return (
    <div className="catalog-list">

      {/* Toolbar */}
      <div className="catalog-toolbar catalog-toolbar--stack">
        <div className="catalog-search-row">
          <input
            type="text"
            placeholder="Rechercher…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="tp-input catalog-search"
            aria-label="Rechercher un format"
          />
          <button
            type="button"
            onClick={handleNew}
            disabled={!canManage}
            title={!canManage ? 'Permission de gestion des collections et séries requise' : undefined}
            className="tp-button tp-button--info"
          >
            + Nouveau
          </button>
        </div>

        <div className="catalog-filter-dropdowns">
          <select
            className="tp-select catalog-filter-select"
            value={sortOrder}
            onChange={e => setSortOrder(e.target.value as any)}
            aria-label="Trier les types de programme"
          >
            <option value="manual">Ordre de tri manuel</option>
            <option value="name-asc">Nom (A-Z)</option>
            <option value="name-desc">Nom (Z-A)</option>
            <option value="count-desc">Nombre de programmes (décroissant)</option>
          </select>
        </div>
      </div>

      <p className="catalog-empty-copy">
        Le format dit comment le programme est produit et livré — plateau, magazine, captation,
        documentaire. Il reste interne : c’est le <strong>genre</strong> qui part au guide des
        programmes.
      </p>

      {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}

      {/* Compteur */}
      <div className="catalog-count" role="status">
        {loading ? <><Spinner /> Chargement…</> : `${filtered.length} format${filtered.length > 1 ? 's' : ''}`}
      </div>

      {/* Liste */}
      <div className="catalog-scroll">
        {loading ? null : filtered.length === 0 ? (
          <div className="catalog-empty">
            {categories.length === 0
              ? 'Aucun format. Créez-en un pour distinguer vos modes de production (Émission, Magazine, Captation, Documentaire…).'
              : 'Aucun résultat'}
          </div>
        ) : (
          filtered.map(category => (
            <ProgramCategoryRow
              key={category.id}
              category={category}
              orderedIds={manualIds}
              canReorder={canReorder}
              reorderHint={searching
                ? 'Effacez la recherche pour réordonner'
                : sortOrder !== 'manual'
                  ? 'Choisissez « Ordre de tri manuel » pour réordonner'
                  : undefined}
              onReorder={ids => void handleReorder(ids)}
              onEdit={(e) => handleEdit(category, e)}
              onDeleted={() => handleDeleted(category.id)}
              canManage={canManage}
            />
          ))
        )}
      </div>

      {showModal && canManage && (
        <ProgramCategoryModal
          channelId={channelId}
          category={editing}
          onCreated={handleCreated}
          onUpdated={handleUpdated}
          onClose={() => { setShowModal(false); setEditing(undefined) }}
        />
      )}
    </div>
  )
})

// ─── ProgramCategoryRow ────────────────────────────────────────────────────────

const ProgramCategoryRow = memo(function ProgramCategoryRow({ category, orderedIds, canReorder, reorderHint, onReorder, onEdit, onDeleted, canManage }: {
  category:     ProgramCategory
  orderedIds:   string[]
  canReorder:   boolean
  reorderHint?: string
  onReorder:    (orderedIds: string[]) => void
  onEdit:       (e: React.MouseEvent) => void
  onDeleted:    () => void
  canManage:    boolean
}) {
  const accentStyle = category.color
    ? { '--catalog-accent': category.color } as CSSProperties
    : undefined
  // Programmes et séries se comptent séparément : les additionner sous le seul
  // mot « programmes » annonçait un nombre de fiches qui n'existe pas.
  const programCount = category._count?.programs ?? 0
  const seriesCount  = category._count?.series ?? 0

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm(`Supprimer le format « ${category.label} » ?\n\nLes programmes ne seront pas supprimés, ils perdront seulement ce format.`)) return
    try {
      await programCategoriesApi.delete(category.id)
      onDeleted()
    } catch (err: any) {
      alert(err.message)
    }
  }

  return (
    <div className="catalog-collection-row">
      <span className="catalog-collection-row__select">
        <span className="catalog-collection-row__dot" style={accentStyle} aria-hidden="true" />
        <span className="catalog-collection-row__copy">
          <span className="catalog-collection-row__title">{category.label}</span>
          <span className="catalog-collection-row__meta">
            {programCount} programme{programCount !== 1 ? 's' : ''}
            {seriesCount > 0 && ` · ${seriesCount} série${seriesCount !== 1 ? 's' : ''}`}
          </span>
        </span>
      </span>

      <div className="catalog-row-actions">
        <TaxonomyReorderButtons
          orderedIds={orderedIds}
          id={category.id}
          label={category.label}
          enabled={canReorder}
          disabledReason={reorderHint}
          onReorder={onReorder}
        />
        <button
          type="button"
          onClick={onEdit}
          disabled={!canManage}
          title={canManage ? 'Modifier' : 'Permission de gestion des collections et séries requise'}
          aria-label={`Modifier ${category.label}`}
          className="catalog-icon-action"
        >
          ✎
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={!canManage}
          title={canManage ? 'Supprimer' : 'Permission de gestion des collections et séries requise'}
          aria-label={`Supprimer ${category.label}`}
          className="catalog-icon-action catalog-icon-action--danger"
        >
          ×
        </button>
      </div>
    </div>
  )
})
